import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User } from '@reflex/shared';
import { ownerKindOf } from '@reflex/shared';
import { usePluginAuth, useOrgKey, usePluginRegistry } from '@reflex/ui/client/context';
import { queryKeys } from '@reflex/ui/client/query-keys';

interface ResolvedOwnerInput {
  ownerId?: string | null;
  ownerName?: string | null;
}

export interface ResolvedOwner {
  ownerId: string | null;
  /**
   * Owner kind string. `'user'` / `'team'` / `'organization'` for the
   * host's built-in kinds; the id's prefix (e.g. `'prj'`) when the
   * owner is plugin-contributed and the host doesn't know the kind.
   * `null` when no `ownerId` is provided (host-tier sentinel).
   */
  ownerKind: string | null;
  ownerName: string | null;
  label: string;
  isCurrentUser: boolean;
}

/**
 * Resolve a single `ownerId` to a display label by looking it up in
 * the active membership's teams and the users query cache. Falls back
 * to a generic label when the prefix is unknown or the lookup misses
 * (which is normal for org-owned rows that don't need a personal name).
 */
export function useResolvedOwner(owner?: ResolvedOwnerInput): ResolvedOwner {
  const { user, activeMembership } = usePluginAuth();
  const orgKey = useOrgKey();
  const queryClient = useQueryClient();
  const registry = usePluginRegistry();
  // `users` is a fetched resource (via `useUsers`), so it lives in the query
  // cache; teams are synchronous auth-context data and come from the active
  // membership directly.
  const users = queryClient.getQueryData<User[]>(queryKeys.users(orgKey));
  const teams = activeMembership?.teams;
  const ownerKinds = registry?.ownerKinds;

  return useMemo<ResolvedOwner>(() => {
    const ownerId = owner?.ownerId ?? null;
    const hostKind = ownerKindOf(ownerId);
    // For plugin-contributed prefixes (e.g. `prj_*`), look up the
    // registered kind name via the plugin owner-kind registry. Falls
    // back to the raw prefix when no plugin claims it so unknown ids
    // still render with a sensible label.
    const idPrefix = ownerId && ownerId.includes('_') ? (ownerId.split('_')[0] ?? null) : null;
    const pluginEntry = idPrefix ? ownerKinds?.get(idPrefix) : undefined;
    const ownerKind: string | null = hostKind ?? pluginEntry?.kind ?? idPrefix;

    if (ownerKind === 'user') {
      const cachedOwner = users?.find((candidate) => candidate.id === ownerId);
      return {
        ownerId,
        ownerKind,
        ownerName: owner?.ownerName ?? cachedOwner?.name ?? cachedOwner?.login ?? null,
        label: owner?.ownerName ?? cachedOwner?.name ?? cachedOwner?.login ?? 'Owner',
        isCurrentUser: !!user?.id && ownerId === user.id,
      };
    }

    if (ownerKind === 'team') {
      const matchedTeam = teams?.find((candidate) => candidate.id === ownerId);
      const ownerName = owner?.ownerName ?? matchedTeam?.name ?? null;
      return {
        ownerId,
        ownerKind,
        ownerName,
        label: ownerName ?? 'Team',
        isCurrentUser: false,
      };
    }

    // Plugin-contributed kind with a registered label: prefer the
    // plugin's label over the generic "Organization" fallback.
    if (pluginEntry) {
      return {
        ownerId,
        ownerKind,
        ownerName: owner?.ownerName ?? null,
        label: owner?.ownerName ?? pluginEntry.label,
        isCurrentUser: false,
      };
    }

    return {
      ownerId,
      ownerKind,
      ownerName: owner?.ownerName ?? null,
      label: owner?.ownerName ?? 'Organization',
      isCurrentUser: false,
    };
  }, [owner, teams, user?.id, users, ownerKinds]);
}
